import { CloseIcon, DownloadIcon } from '@chakra-ui/icons'
import {
  Box,
  BoxProps,
  Button,
  HStack,
  IconButton,
  Spinner,
  Stack,
} from '@chakra-ui/react'

import { CandidateCVFile } from '@pkg/types/interview'

type CandidateAttachmentsProps = BoxProps & {
  files?: CandidateCVFile[]
  /** Files still uploading: shown as the same pills, with a spinner. */
  uploadingFileNames?: string[]
  canManage?: boolean
  onRemove?: (file: CandidateCVFile) => void
}

/**
 * The candidate's CV files as the same white pill buttons the rest of the
 * card uses (View on LinkedIn, Edit...): click downloads the file (the signed
 * URL carries Content-Disposition: attachment), the round button removes it.
 */
export const CandidateAttachments = ({
  files = [],
  uploadingFileNames = [],
  canManage,
  onRemove,
  ...boxProps
}: CandidateAttachmentsProps) => {
  if (!files.length && !uploadingFileNames.length) {
    return null
  }

  return (
    <Stack spacing={3} align="flex-start" {...boxProps}>
      {files.map((file) => {
        const displayName = getDisplayFileName(file.fileName)

        return (
          <HStack key={file.id ?? file.fileKey} spacing={2} maxW="full">
            <Button
              as="a"
              href={file.fileUrl}
              download={displayName}
              variant={'brand-solid-white'}
              rounded={'full'}
              leftIcon={<DownloadIcon />}
              title={displayName}
              minW={0}
            >
              <Box as="span" overflow="hidden" textOverflow="ellipsis">
                {displayName}
              </Box>
            </Button>
            {canManage ? (
              <IconButton
                aria-label={`Remove ${displayName}`}
                icon={<CloseIcon boxSize={2.5} />}
                variant={'brand-solid-white'}
                rounded={'full'}
                size="sm"
                flexShrink={0}
                onClick={() => onRemove?.(file)}
              />
            ) : null}
          </HStack>
        )
      })}
      {uploadingFileNames.map((fileName, index) => (
        <Button
          key={`${fileName}-${index}`}
          as="div"
          variant={'brand-solid-white'}
          rounded={'full'}
          leftIcon={<Spinner size="xs" />}
          color={'blackAlpha.600'}
          pointerEvents="none"
          title={fileName}
          minW={0}
          maxW="full"
        >
          <Box as="span" overflow="hidden" textOverflow="ellipsis">
            {fileName}
          </Box>
        </Button>
      ))}
    </Stack>
  )
}

const getDisplayFileName = (fileName: string): string => {
  if (!fileName) {
    return 'Untitled file'
  }

  if (!needsEncodingFix(fileName)) {
    return fileName
  }

  try {
    const charCodes = Array.from(fileName).map((char) => char.charCodeAt(0))
    const bytes = new Uint8Array(charCodes)
    const decoded = new TextDecoder('utf-8').decode(bytes)
    return decoded || fileName
  } catch (error) {
    console.error('Failed to decode candidate file name', error)
    return fileName
  }
}

const needsEncodingFix = (value: string): boolean => {
  return /[ÃÂÐÑ]/.test(value)
}
